import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import { useSite } from '../lib/content';
import ReservationModal from './ReservationModal';

const Navbar = () => {
  const site = useSite();
  const nav = Array.isArray(site.nav) ? site.nav : [];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(() => typeof window !== 'undefined' && window.scrollY > 40);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveLock, setReserveLock] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const transparent = isHome && !scrolled && !open;

  const openReservation = () => {
    const p = location.pathname;
    if (p.startsWith('/tavern')) setReserveLock('tavern');
    else if (p.startsWith('/steakhouse')) setReserveLock('steakhouse');
    else setReserveLock(null);
    setReserveOpen(true);
  };

  // Lock body scroll while the mobile menu is open so the page (and header
  // transparency) stays put — the menu looks identical until it's closed.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const reserveLabel = site.navReserveLabel || 'Reservations';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          transparent ? 'bg-transparent' : 'bg-[#f7f1e3]/95 backdrop-blur-md border-b border-[#942222]/10'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[88px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo-link">
            <span className="transition-transform duration-500 group-hover:rotate-[-4deg]">
              <Logo size={56} />
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                data-testid={`navbar-link-${n.to.replace('/', '') || 'home'}`}
                className={({ isActive }) =>
                  `text-[11px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 hover:opacity-70 ${
                    transparent ? 'text-[#f7f1e3]' : 'text-[#942222]'
                  } ${isActive ? 'opacity-100' : 'opacity-90'}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={openReservation}
              data-testid="navbar-reserve-button"
              className="px-7 py-[14px] bg-[#942222] text-[#f7f1e3] text-[11px] tracking-[0.22em] font-semibold uppercase border border-[#942222] hover:bg-[#b13030] transition-colors"
            >
              {reserveLabel}
            </button>
          </nav>

          <button
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            data-testid="navbar-mobile-menu-open"
            className={`lg:hidden p-2 ${transparent ? 'text-[#f7f1e3]' : 'text-[#942222]'}`}
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      {/* Mobile menu — rendered OUTSIDE <header> so the header's backdrop-blur
          doesn't trap this fixed overlay (it must always cover the full viewport,
          identical whether or not the page has been scrolled). */}
      <div
        data-testid="navbar-mobile-menu"
        className={`lg:hidden fixed inset-0 bg-[#942222] z-[70] transition-transform duration-500 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
      >
        <div className="flex items-center justify-between px-6 h-[88px]">
          <Logo size={48} />
          <button
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            data-testid="navbar-mobile-menu-close"
            className="text-[#f7f1e3] p-2"
          >
            <X size={26} />
          </button>
        </div>
        <nav className="flex flex-col items-center justify-center gap-8 mt-12">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              onClick={() => setOpen(false)}
              data-testid={`navbar-mobile-link-${n.to.replace('/', '') || 'home'}`}
              className="text-[#f7f1e3] text-3xl font-serif tracking-wide"
            >
              {n.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => { setOpen(false); openReservation(); }}
            data-testid="navbar-mobile-reserve-button"
            className="mt-8 px-10 py-3 bg-[#e2b95a] text-[#942222] text-sm tracking-[0.22em] font-semibold uppercase"
          >
            {reserveLabel}
          </button>
        </nav>
      </div>

      <ReservationModal
        open={reserveOpen}
        onClose={() => setReserveOpen(false)}
        lockVenue={reserveLock}
      />
    </>
  );
};

export default Navbar;
